import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, ElementRef,
  EventEmitter, HostBinding, Input, OnDestroy, Optional, Output, Self, ViewChild
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { Account, AccountService, AssignmentService, Contact } from '@core/api';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MultiAccountDialogComponent } from '../multi-account-dialog/multi-account-dialog.component';
import { SharedModule } from '@shared/modules';
import { AutocompletePositionDirective } from '@shared/directives';

@Component({
  selector: 'net-select-account',
  templateUrl: './select-account.component.html',
  styleUrls: ['./select-account.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectAccountComponent
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SharedModule,
    AutocompletePositionDirective
  ]
})
export class SelectAccountComponent implements OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<Account | null> {

  static nextId = 0;

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'select-account';
  describedBy = '';
  stateChanges = new Subject<void>();

  accounts: Account[] = [];
  inputCtrl = new UntypedFormControl();
  isSelected = false;

  isNotClickable = false;

  @ViewChild('auto') auto: MatAutocomplete;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input() isClickable = true;

  @Input()
  get salesOrganizationId(): string { return this._salesOrganizationId; }
  set salesOrganizationId(value: string) {
    this._salesOrganizationId = value;
    this.accounts = [];
    this.stateChanges.next();
  }
  private _salesOrganizationId: string;

  @Input() tabIndex;

  @Input() panelWidth: string | number;

  @Input() type: string;

  @Input() contact: Contact;

  @Input() dialogClose: boolean;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    if (value) {
      this.inputCtrl.disable();
    } else {
      this.inputCtrl.enable();
    }

    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): Account | null { return this._value; }
  set value(value: Account | null) {
    this._value = value;
    this.onChange(value);
    this.stateChanges.next();
  }
  private _value: Account;

  @Input()
  get click(): boolean { return this._click; }
  set click(value: boolean) {
    this._click = value;
    this.stateChanges.next();
  }
  private _click = true;

  @Output() selectionChange: EventEmitter<Account> = new EventEmitter<Account>();

  @HostBinding('id') id = `select-account-${SelectAccountComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedByBinding = this.describedBy;

  get shouldLabelFloat() { return this.focused || !this.empty; }

  get empty() { return !this.value; }

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private accountService: AccountService,
    private assignmentService: AssignmentService,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    @Optional() @Self() public ngControl: NgControl
  ) {
    // Material form field implementation
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    // Listen input control value changes and filter results
    this.inputCtrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter(value => typeof value === 'string'),
      map(value => value.toString().trim()),
      filter(value => value.length > 2),
      tap(() => {
        this.loading = true;
        this.accounts = [];
        // this.value = null;
      }),
      switchMap(value => this.accountService.elasticSearch({
        filter: {
          accountName: value,
          salesOrganizationId: this.salesOrganizationId
        },
        pageSize: 50
      })),
      tap(() => this.loading = false)
    ).subscribe(response => {
      this.accounts = response.data.results;
      this.cdr.detectChanges();
    });

    if (this.router.url.includes('/new')) {
      this.isNotClickable = true;
    }

  }

  displayName = (account: Account) => account ? account.name : '';

  reset() {
    if (this.inputCtrl.pristine) {
      return;
    }

    this.value = null;
    this.accounts = [];
    this.inputCtrl.setValue(null);
    this.inputCtrl.markAsPristine();
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    this.accountService.get(event.option.value.accountId).subscribe(response => {
      this.value = response.data;
      this.inputCtrl.setValue(this.value);
      this.input.nativeElement?.blur();
      this.selectionChange.emit(this.value);
    });
  }

  onBlur() {

    if (!this.isSelected && this.accounts.length === 0) {
      this.inputCtrl.setValue(null);
      this.accounts = [];
    }
    // On blur with nothing selected reset component
    if (this.ngControl.dirty && !this.auto.isOpen && !this.value) {
      this.reset();
    }
  }

  onClick() {
    // if (this.value || this.value.accountId) {
    //   return;
    // }
    if (this.disabled && this.value && this.click) {
      if (this.type === 'contact') {
        this.onClickFromContact();
        return;
      }

      if (this.dialogClose) {
        this.assignmentService.closeDialog(true);
      }

      if (this.value.accountId) {
        this.router.navigate(['/leads', this.value.accountId]);
      } else if (this.value['leadDraft']) {
        this.router.navigate(['/leads', this.value['leadDraft'].leadDraftId]);
      }
    }
  }

  onClickFromContact() {
    if (this.contact?.customer?.accounts?.length > 1) {
      this.dialog
        .open(MultiAccountDialogComponent, {
          autoFocus: false,
          data: { accounts: this.contact.customer.accounts }
        })
        .addPanelClass('cdk-full-overlay');
    } else if (this.contact?.customer?.accounts?.length === 1) {
      this.router.navigate(['/leads', this.contact?.customer?.accounts[0]?.accountId]);
    } else {
      this.router.navigate(['/leads-drafts', this.contact?.customer?.leadDraft?.leadDraftId]);
    }
  }

  onClosed() {
    // On panel close with nothing selected reset component
    if (this.ngControl.dirty && !this.value) {
      this.reset();
    }

    if (!this.isSelected) {
      this.inputCtrl.setValue(null);
      this.accounts = [];
    }
  }

  onChange = (_: any) => { };
  onTouched = () => { };
  onContainerClick = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');

  writeValue(value: Account) {
    this.value = value;
    this.inputCtrl.setValue(value);
  }

  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }
}
